import React, { useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { normalizePhoneNumber } from 'helpers/rules'

// Helpers
import get from 'lodash/get'
import { number } from 'helpers/format'

// Components
import FormGroup from 'react-bootstrap/FormGroup'
import FormLabel from 'react-bootstrap/FormLabel'
import FormControl from 'react-bootstrap/FormControl'

// Styles
import './InputPassword.scss'
import IconCheck from 'ui/Icons/IconCheckMark'

//UI
import EyeSlash from 'ui/Icons/EyeSlash'
import IconEye from 'ui/Icons/IconEye'
import Button from 'ui/Button'

export const InputPassword = ({
  phoneNumber,
  name,
  label,
  placeholder,
  value,
  errors,
  readOnly,
  textarea,
  rows,
  maxlength,
  counter,
  append,
  autoComplete,
  resize,
  bottomLeft,
  onChange,
  onBlur,
  ...props
}) => {
  // States
  const [hasNineCharacters, setHasNineCharacters] = useState(false)
  const [hasUppercaseLetter, setHasUppercaseLetter] = useState(false)
  const [hasLowercaseLetter, setHasLowercaseLetter] = useState(false)
  const [hasNumber, setHasNumber] = useState(false)
  const [hasSpecialCharacter, setHasSpecialCharacter] = useState(false)
  const [showPassword, setShowPassword] = useState(true)
  // Validate error
  const error = get(errors, name, null)

  const inputRef = useRef(null)

  // Counter text
  const counterText = useMemo(() => {
    const length = number(value ? value.length : 0)
    const suffix = typeof counter === 'string' ? counter : ''

    if (maxlength) {
      return `${length}/${number(maxlength)} ${suffix}`
    }

    return length
  }, [counter, maxlength, value])

  const handleChange = (value) => {
    if (phoneNumber) {
      value = normalizePhoneNumber(value)
    }

    if (maxlength) {
      value = value.slice(0, maxlength)
    }
    setHasNineCharacters(value.length >= 9)
    setHasUppercaseLetter(/[A-Z]/.test(value))
    setHasLowercaseLetter(/[a-z]/.test(value))
    setHasNumber(/\d/.test(value))
    setHasSpecialCharacter(/[$&+,:;=?@#|'<>.^*()%!-]/.test(value))

    onChange(value, name)

    try {
      const input = inputRef.current
      const start = input.selectionStart
      const end = input.selectionEnd

      // Prevent if cursor is in the end
      if (start === end && end === input.value.length) {
        return
      }

      setTimeout(() => {
        input.setSelectionRange(start, end)
      }, 0)
    } catch (err) {
      //
    }
  }
  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }
  const green = '#25b41c'
  const gray = '#808080'
  const black = '#000000'
  const textSize = 14
  const divHeight = 24
  const textMargin = 6
  const validSpecialCharacter = hasSpecialCharacter ? green : gray
  const validUpperCase = hasUppercaseLetter ? green : gray
  const validLowerCase = hasLowercaseLetter ? green : gray
  const validNumber = hasNumber ? green : gray
  const validLength = hasNineCharacters ? green : gray
  const icon = showPassword ? <EyeSlash></EyeSlash> : <IconEye></IconEye>
  const textType = !showPassword ? 'text' : 'password'

  return (
    <FormGroup className="x-pwInput">
      <div style={{ padding: 0, height: divHeight, alignItems: 'center' }}>
        <IconCheck color={validUpperCase} />
        <span
          className="label"
          style={{ color: black, marginLeft: textMargin, fontSize: textSize }}
        >
          Uppercase letter
        </span>
      </div>
      <div style={{ padding: 0, height: divHeight, alignItems: 'center' }}>
        <IconCheck color={validLowerCase} />
        <span className="label" style={{color:black,marginLeft:textMargin,fontSize:textSize}}>
          Lowercase letter
        </span>
      </div>
      <div style={{padding:0,height:divHeight,alignItems:'center'}}>
        <IconCheck
          color={validNumber}>
        </IconCheck> 
        <span className="label" style={{color:black,marginLeft:textMargin,fontSize:textSize}}>
          Number
        </span>
      </div>           
      <div style={{padding:0,height:divHeight,alignItems:'center'}}>
        <IconCheck
        color={validSpecialCharacter}>
        </IconCheck> 
        <span className="label" style={{color:black,marginLeft:textMargin,fontSize:textSize}}>
          Special character
        </span>
      </div>
      <div style={{padding:0,height:divHeight,alignItems:'center'}}>
        <IconCheck
          color={validLength}
          marginLeft={textMargin}
          >
        </IconCheck> 
        <span className="label" style={{color:black,marginLeft:textMargin,fontSize:textSize}}>
            With at least 9 characters
        </span>
      </div>
      <ul></ul>
      {!!label && <FormLabel>{label}</FormLabel>}
      <div className="position-relative">
        <FormControl
          className="d-inline ms-6"
          ref={inputRef}
          name={name}
          type={textType}
          placeholder={placeholder}
          value={value}
          isInvalid={!!error}
          readOnly={readOnly}
          plaintext={readOnly}
          as={textarea ? 'textarea' : 'input'}
          rows={rows}
          maxLength={maxlength}
          autoComplete={autoComplete}
          style={{ resize }}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={() => onBlur(name)}
          {...props}
        />
        <Button 
        variant="link"
        onClick={toggleShowPassword}
        className="d-inline"
        style={{ position: 'absolute', right: 15, top: 0 }}
      >
        {icon}
      </Button>
        <div className="append">{append}</div>
      </div>
    

      {!!bottomLeft && (
        <div className="input-meta">
          <div className="input-bottom-left input-error">{error}</div>
        </div>
      )}

      <div className="input-meta">
        <div className="input-bottom-left">
          {bottomLeft ? bottomLeft : <div className="input-error">{error}</div>}
        </div>

        {counter && <div className="input-counter">{counterText}</div>}
      </div>
    </FormGroup>
  )
}

InputPassword.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  errors: PropTypes.object,
  readOnly: PropTypes.bool,
  textarea: PropTypes.bool,
  rows: PropTypes.number,
  maxlength: PropTypes.number,
  append: PropTypes.node,
  counter: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  autoComplete: PropTypes.string,
  phoneNumber: PropTypes.bool,
  resize: PropTypes.string,

  bottomLeft: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),

  // Events
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
}

InputPassword.defaultProps = {
  name: '',
  label: '',
  placeholder: '',
  value: '',
  errors: {},
  readOnly: false,
  textarea: false,
  rows: 3,
  maxlength: 30,
  counter: false,
  autoComplete: 'off',
  append: null,
  phoneNumber: false,
  resize: 'none',

  bottomLeft: null,

  // Events
  onChange: () => {},
  onBlur: () => {},
}

export default InputPassword
